import { VybeUser } from "./types";

/** Resolve the CRM base URL (hosts the cross-origin auth API), same convention as the gateways lib. */
export function crmBaseUrl(): string {
  const viteUrl = import.meta.env.VITE_CRM_URL;
  if (viteUrl) return viteUrl;
  if (typeof process !== "undefined" && process.env) {
    return process.env.VITE_CRM_URL || process.env.NEXT_PUBLIC_CRM_URL || "";
  }
  return "";
}

function url(path: string): string {
  return `${crmBaseUrl().replace(/\/$/, "")}${path}`;
}

/** Origin of the CRM (used to pin the Google popup's postMessage sender). Empty when unconfigured. */
export function crmOrigin(): string {
  const base = crmBaseUrl();
  if (!base) return "";
  try {
    return new URL(base, typeof window !== "undefined" ? window.location.href : undefined).origin;
  } catch {
    return "";
  }
}

/**
 * Entry point for the terminal Google flow (proposal 71). `flow=terminal` tells the CRM callback to end
 * in the bridge page with a single-use handoff code instead of a CRM cookie session.
 */
export function googleSignInUrl(flow: string = "terminal"): string {
  return url(`/auth/google?flow=${encodeURIComponent(flow)}`);
}

/** Which external sign-in doors the CRM has configured (`{ google: boolean }`). */
export async function fetchAuthProviders(): Promise<{ google: boolean }> {
  const res = await fetch(url("/api/auth/providers"));
  if (!res.ok) throw new Error(`Provider lookup failed (${res.status})`);
  return (await res.json()) as { google: boolean };
}

/** Email a one-time sign-in code (reuses the CRM passwordless OTP). */
export async function requestCode(email: string): Promise<void> {
  const res = await fetch(url("/api/auth/request"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(`Could not send code (${res.status})`);
}

/** Verify the code and receive an opaque session token + user. */
export async function verifyCode(
  email: string,
  code: string,
): Promise<{ token: string; user: VybeUser }> {
  const res = await fetch(url("/api/auth/verify"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code }),
  });
  if (res.status === 401) throw new Error("Incorrect or expired code");
  if (!res.ok) throw new Error(`Verification failed (${res.status})`);
  return (await res.json()) as { token: string; user: VybeUser };
}

/** Redeem a single-use CRM→terminal SSO handoff code for a session token + user. */
export async function redeemHandoff(
  code: string,
): Promise<{ token: string; user: VybeUser }> {
  const res = await fetch(url("/api/auth/handoff/redeem"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (res.status === 401) throw new Error("Handoff code invalid or expired");
  if (!res.ok) throw new Error(`Handoff redemption failed (${res.status})`);
  return (await res.json()) as { token: string; user: VybeUser };
}

/**
 * Terminal → CRM reverse handoff (proposal 52): trade the bearer token for a single-use,
 * short-TTL portal handoff code. The caller opens `{portal}/auth/portal?code=…`, which signs
 * the CRM cookie in without an OTP.
 */
export async function mintPortalHandoff(token: string): Promise<string> {
  const res = await fetch(url("/api/auth/portal-handoff"), {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Portal handoff failed (${res.status})`);
  const { code } = (await res.json()) as { code: string };
  if (!code) throw new Error("Portal handoff returned no code");
  return code;
}

/**
 * A CRM route rejected our bearer token. Distinguished from a transport failure because the two demand
 * opposite responses: an expired session must DROP the synced accounts (we no longer have the right to
 * show them), while an unreachable CRM must leave them alone.
 */
export class VybeAuthExpiredError extends Error {
  constructor() {
    super("Your Vybe Trader session has expired");
    this.name = "VybeAuthExpiredError";
  }
}

/** The gateway identity every synced account is fronted by (minted from the CRM's own connect config). */
export interface SyncedAccountGateway {
  host: string;
  port: number;
  hubUrl?: string | null;
  gatewaySlug?: string | null;
  logoUrl?: string | null;
  companyName: string;
  serverName: string;
}

/** One trading account the signed-in user owns, as the CRM describes it. Carries NO password. */
export interface SyncedAccount {
  login: number;
  name?: string | null;
  mode?: string | null;
  currency?: string | null;
  balance?: string | null;
  group?: string | null;
  isDemo?: boolean;
  createdAt?: string | null;
}

export interface MyAccountsResponse {
  ok: boolean;
  error?: string | null;
  gateway?: SyncedAccountGateway;
  accounts: SyncedAccount[];
}

/**
 * Every trading account registered to the signed-in user — the same list the client portal shows.
 * Requires a session token by construction: signed out, there is nothing to ask with and nothing to show.
 */
export async function fetchMyAccounts(token: string): Promise<MyAccountsResponse> {
  const res = await fetch(url("/api/auth/my-accounts"), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new VybeAuthExpiredError();
  if (!res.ok) throw new Error(`Account sync failed (${res.status})`);
  const body = (await res.json()) as MyAccountsResponse;
  return { ...body, accounts: body.accounts ?? [] };
}

/**
 * Mint a connect token for one account the user owns, so the terminal can connect it without ever having
 * been told its password. Rotates that account's password server-side, so callers must only reach for this
 * when they have no stored credential for the login (see the CRM route's note).
 */
export async function mintAccountConnect(token: string, login: number): Promise<string> {
  const res = await fetch(url(`/api/auth/my-accounts/${login}/connect`), {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new VybeAuthExpiredError();
  if (res.status === 404) throw new Error("That account is no longer on your profile");
  if (!res.ok) throw new Error(`Could not prepare that account (${res.status})`);
  const { connect } = (await res.json()) as { connect?: string };
  if (!connect) throw new Error("The server returned no connection details");
  return connect;
}

export async function logout(token: string): Promise<void> {
  try {
    await fetch(url("/api/auth/logout"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
  } catch {
    // best-effort; local state is cleared regardless
  }
}
